import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { getParcels, deleteParcel } from '../../api/parcels';
import { useParcelStore } from '../../store/parcelStore';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import { FullPageSpinner } from '../../components/common/Spinner';

export default function ParcelList() {
  const navigate = useNavigate();
  const { setParcels, removeParcel } = useParcelStore();
  const [loading, setLoading] = useState(true);
  const [displayParcels, setDisplayParcels] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    getParcels(search ? { search } : undefined)
      .then((res) => {
        const data = res.data?.results ?? res.data;
        setDisplayParcels(data);
        if (!search) setParcels(data);
      })
      .catch(() => toast.error('Неуспешно вчитување на парцелите'))
      .finally(() => setLoading(false));
  }, [setParcels, search]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteParcel(deleteTarget.id);
      removeParcel(deleteTarget.id);
      setDisplayParcels((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      toast.success(`Парцелата "${deleteTarget.parcel_name}" е избришана`);
      setDeleteTarget(null);
    } catch {
      toast.error('Неуспешно бришење на парцелата');
    } finally {
      setDeleting(false);
    }
  };

  if (loading && displayParcels.length === 0) return <FullPageSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Парцели</h1>
          <p className="text-gray-500 text-sm mt-1">
            {displayParcels.length} парцел{displayParcels.length !== 1 ? 'и' : 'а'}{search ? ' пронајдени' : ' регистрирани'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Пребарај по назив или култура..."
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 w-56"
          />
          <Button onClick={() => navigate('/parcels/new')}>+ Додај парцела</Button>
        </div>
      </div>

      {displayParcels.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-16 text-center">
          {search ? (
            <p className="text-gray-400 mb-4">Нема парцели кои одговараат на &ldquo;{search}&rdquo;.</p>
          ) : (
            <>
              <p className="text-gray-400 mb-4">Сè уште нема парцели. Додајте го вашето прво поле за да започнете.</p>
              <Button onClick={() => navigate('/parcels/new')}>+ Додај парцела</Button>
            </>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {['Име', 'Вид на култура', 'Површина (м²)', 'Геогр. ширина', 'Геогр. должина', 'Акции'].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-gray-500 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayParcels.map((parcel) => (
                <tr key={parcel.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3">
                    <Link to={`/parcels/${parcel.id}`} className="font-medium text-green-700 hover:underline">
                      {parcel.parcel_name}
                    </Link>
                  </td>
                  <td className="px-5 py-3"><Badge color="green">{parcel.crop_type || '—'}</Badge></td>
                  <td className="px-5 py-3 text-gray-600">{parcel.size_m2}</td>
                  <td className="px-5 py-3 text-gray-500 font-mono text-xs">{parseFloat(parcel.latitude).toFixed(5)}</td>
                  <td className="px-5 py-3 text-gray-500 font-mono text-xs">{parseFloat(parcel.longitude).toFixed(5)}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/parcels/${parcel.id}/edit`)}>Уреди</Button>
                      <Button variant="danger" size="sm" onClick={() => setDeleteTarget(parcel)}>Избриши</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Избриши парцела" size="sm">
        <p className="text-gray-600 text-sm mb-5">
          Дали сте сигурни дека сакате да ја избришете <strong>{deleteTarget?.parcel_name}</strong>? Оваа акција не може да се врати.
        </p>
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Откажи</Button>
          <Button variant="danger" loading={deleting} onClick={handleDelete}>Избриши</Button>
        </div>
      </Modal>
    </div>
  );
}
